import React, {
  useMemo,
  useCallback,
  useRef,
  useState
} from "react";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  CircularProgress,
  Tooltip
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import ImageCarousel from "components/modals/ImageCarousel/ImageCarousel";
import CustomText from "components/common/CustomText/CustomText";
import { ImageItem } from "types/types/Types";
import { colors } from "styles/colors";
import useGlobalStyles from "styles/globalStyles";
import AddIcon from "icons/AddIcon";
import { useAuth } from "context/AuthContext";
import { useUpdateProcedureImages } from "hooks/useProcedure";
import { snackbarService } from "services/snackbarService";
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import { hexWithOpacity } from "common/utils";
import CaseImage from "components/case/CaseImage/CaseImage";
import { useUpdateRegistryPatientImages } from "hooks/useRegistryPatient";


interface Props {
  images: ImageItem[];
  setImages: (updater: any) => void;
  procedureId: number;
  refetch: () => void;
  isFetching: boolean;
  isEditor: boolean;
}

export default function RegistryPatientImagesDetails({
  images,
  setImages,
  procedureId,
  refetch,
  isFetching,
  isEditor
}: Props) {
  const location = useLocation();
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();
  const { registryId, patientId } = useParams();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const { mutate: updateImagesMutation, isLoading } =
    useUpdateRegistryPatientImages(
      () => {
        snackbarService.showSnackbar("Images have been updated successfully", "success");
        setIsEditMode(false);
        refetch();
      },
      (error: any) => console.error(error)
    );

  const routeName = useMemo(
    () => location.pathname ?? "",
    [location.pathname]
  );

  const isEditable = useMemo(
    () => routeName.includes("AddCase"),
    [routeName]
  );

  const handleRemove = useCallback(
    (index: number) => {
      setImages((prev: ImageItem[]) =>
        prev.filter((_, i) => i !== index)
      );
    },
    [setImages]
  );

  const handleRename = useCallback(
    (index: number, newName: string) => {
      setImages((prev: ImageItem[]) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = { ...updated[index], image_name: newName };
        return updated;
      });
    },
    [setImages]
  );

  const handleAddPhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const imageUrl = URL.createObjectURL(file);

      const newImage: ImageItem = {
        uri: imageUrl,
        image_name: `Image-${images.length + 1}`
      } as any;

      setImages((prev: ImageItem[]) => [...prev, newImage]);

      e.target.value = "";
    },
    [images.length, setImages]
  );

  const hasNewImages = images.some(img => !img.id);

  const handleUpdateImages = useCallback(() => {
    const newImages = images.filter(img => !img.id);

    if (newImages.length === 0) {
      snackbarService.showSnackbar("Images have been saved successfully", "success");
      setIsEditMode(false);
      return;
    }

    updateImagesMutation({
      dto: {
        registryId: Number(registryId),
        patientId: Number(patientId),
      },
      images: newImages
    });
  }, [images, procedureId, updateImagesMutation, user.id]);

  return (
    <>
      <Box className={globalStyles.sectionContainer}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={globalStyles.sectionHeader}
        >
          <CustomText
            text="Images"
            color={colors.primary}
            fontWeight={600}
          />

          {isEditor && (
            <Stack direction="row" alignItems="center" mr={2} gap={1}>
              {isEditMode ? (
                <>
                  <Tooltip title="Save">
                    <IconButton
                      onClick={handleUpdateImages}
                      className={globalStyles.sectionButton}
                      disabled={isLoading}
                      sx={{
                        backgroundColor: `${hexWithOpacity(colors.success, 0.1)} !important`,
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <FileDownloadDoneIcon color="success" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <IconButton
                    onClick={handleAddPhotoClick}
                    className={globalStyles.sectionButton}
                  >
                    <AddIcon size={22} color={colors.primary} />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  onClick={() => setIsEditMode(true)}
                  className={globalStyles.sectionButton}
                >
                  <DriveFileRenameOutlineIcon
                    sx={{ fontSize: 22, color: colors.primary }}
                  />
                </IconButton>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelected}
              />
            </Stack>
          )}
        </Stack>

        {images.length > 0 && (
          <>
            <Divider />

            {/* Image List */}
            <Grid container spacing={1.5} sx={{ px: 2.5, my: 2 }}>
              {images.map((image, index) => (
                <Grid
                  key={`${image?.id ?? "local"}-${index}`}
                  size={{ xs: 12, md: 6, lg: 4, xl: 2 }}
                >
                  <Box sx={{ mt: 1, cursor: "pointer" }}>
                    <CaseImage
                      index={index}
                      item={image}
                      handleRename={handleRename}
                      handleRemove={handleRemove}
                      isEditMode={isEditMode}
                      onPreview={() => {
                        setCarouselIndex(index);
                        setCarouselOpen(true);
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>

      <ImageCarousel
        open={carouselOpen}
        images={images}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />
    </>
  );
}
